import { get, set, del } from 'idb-keyval';
import type { PersistedClient, Persister } from '@tanstack/react-query-persist-client';
import { getStoredUser } from '@/lib/auth';

export const PERSIST_KEY = 'nomi-rq-cache';

// Bust the persisted cache whenever the built assets change (a deploy may change
// response shapes; #397's zod schemas would otherwise parse-throw on a stale
// hydrated payload). import.meta.env.VITE_BUILD_ID is injected at build; fall
// back to MODE so dev doesn't churn.
export const CACHE_BUSTER: string =
  (import.meta.env.VITE_BUILD_ID as string | undefined) ?? import.meta.env.MODE;

// Tracks which user's data currently lives in the persisted cache. The cache
// holds financial data, so on a shared device we must never let one user's
// hydrated queries render for another user — useAuth compares this against
// the freshly-established user.id and busts the cache on a mismatch.
const OWNER_KEY = 'nomi-rq-owner';

/** IndexedDB-backed persister (idb-keyval). IDB, not localStorage, because the
 *  finance cache exceeds the ~5MB Web Storage budget. */
export function createIDBPersister(key: IDBValidKey = PERSIST_KEY): Persister {
  return {
    persistClient: async (client: PersistedClient) => { await set(key, client); },
    // Owner-gate the restore so the persist library's own restoreClient() can
    // never hydrate another user's financial blob — independent of any effect
    // timing in useAuth. PersistQueryClientProvider calls this to seed the
    // in-memory cache; if the persisted owner doesn't match the user currently
    // stored in localStorage (or there is no stored user), we drop the blob and
    // its owner key and hydrate nothing. When the owner matches (or none is
    // recorded), we return the stored client so the library's buster/maxAge
    // checks run exactly as before.
    restoreClient: async () => {
      const persistedOwner = await get<number>(OWNER_KEY);
      if (persistedOwner !== undefined) {
        const storedUser = getStoredUser();
        if (!storedUser || storedUser.id !== persistedOwner) {
          await del(key);
          await del(OWNER_KEY);
          return undefined;
        }
      }
      return get<PersistedClient>(key);
    },
    removeClient: async () => { await del(key); },
  };
}

// Shared singleton — imported by App.tsx (provider) and useAuth.tsx (logout /
// user-switch bust), so both act on the same persisted store.
export const persister = createIDBPersister();

export const setCacheOwner = (userId: number): Promise<void> => set(OWNER_KEY, userId);

export const getCacheOwner = (): Promise<number | undefined> => get<number>(OWNER_KEY);

export async function clearPersistedCache(persisterToClear: Pick<Persister, 'removeClient'>): Promise<void> {
  await persisterToClear.removeClient();
  await del(OWNER_KEY);
}
